/*
 * map-layer.js — GPS tracks on a Leaflet map, drawn on ONE canvas.
 *
 *   var layer = GpsMapLayer.create(L, map, { onHover, onClick, onWaypoint })
 *   layer.setTracks([{ id, sr, color, visible, values, scale }])
 *   layer.setSelection(id, from, to) / layer.setCursor(id, idx) / layer.setWaypoints(list)
 *   layer.setHeat(lines|null)            — the personal heat map mode
 *   layer.fit(ids?)                      — fit the view to the tracks
 *
 * Why a canvas and not L.polyline: a coloured track is a polyline PER POINT
 * (colour changes along it), and a 100 000-point ride as SVG paths freezes the
 * tab. Here every track is projected once to Web-Mercator pixels at zoom 0,
 * scaled per zoom, thinned to what a pixel can show, and drawn in colour runs.
 * The canvas sits in the overlay pane and follows Leaflet's zoom animation
 * with the same transform Leaflet's own renderer uses.
 */
(function () {
    'use strict';
    var root = window;
    var TILE = 256, RAD = Math.PI / 180, MAXLAT = 85.0511287798;

    function projX(lon) { return (lon + 180) / 360 * TILE; }
    function projY(lat) {
        lat = Math.max(-MAXLAT, Math.min(MAXLAT, lat));
        var s = Math.sin(lat * RAD);
        return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * TILE;
    }

    /* Palettes. Sequential for magnitudes (speed, heart rate…), diverging for
     * grade — down is blue, flat is neutral, up runs to red — so a steep
     * descent never reads as «fast». Stops chosen for contrast on both the
     * light and the dark basemap (≥ 3:1 against #fafaf8 and #0d0f14). */
    var SEQ = [[0, [49, 110, 220]], [0.25, [22, 160, 190]], [0.5, [60, 180, 75]], [0.75, [240, 170, 20]], [1, [220, 40, 40]]];
    var DIV = [[0, [30, 90, 200]], [0.35, [80, 160, 220]], [0.5, [150, 150, 150]], [0.65, [240, 170, 30]], [1, [200, 20, 30]]];
    function ramp(stops, t) {
        if (!(t >= 0)) t = 0; else if (t > 1) t = 1;
        for (var i = 1; i < stops.length; i++) {
            if (t <= stops[i][0]) {
                var a = stops[i - 1], b = stops[i], f = (t - a[0]) / (b[0] - a[0]);
                return [a[1][0] + (b[1][0] - a[1][0]) * f, a[1][1] + (b[1][1] - a[1][1]) * f, a[1][2] + (b[1][2] - a[1][2]) * f];
            }
        }
        return stops[stops.length - 1][1];
    }
    var BUCKETS = 48;
    function bucketColors(diverging) {
        var out = [];
        for (var i = 0; i < BUCKETS; i++) {
            var c = ramp(diverging ? DIV : SEQ, i / (BUCKETS - 1));
            out.push('rgb(' + Math.round(c[0]) + ',' + Math.round(c[1]) + ',' + Math.round(c[2]) + ')');
        }
        return out;
    }
    var SEQ_C = bucketColors(false), DIV_C = bucketColors(true);

    function create(L, map, opts) {
        opts = opts || {};
        var tracks = [], sel = null, cursor = null, wpts = [], heat = null, skin = 'dark', markers = true, kmMarks = null;
        var hl = null, marks = null; // hovered range (amber halo) · climb marks [{id, from, to, label, color}]
        var canvas = document.createElement('canvas');
        canvas.className = 'gt-canvas leaflet-zoom-animated';
        canvas.style.position = 'absolute';
        canvas.style.pointerEvents = 'none';
        var ctx = canvas.getContext('2d');
        var pane = map.getPanes().overlayPane;
        pane.appendChild(canvas);
        var PAD = 0.15;
        var bounds = null, zoomNow = map.getZoom(), centerNow = map.getCenter();
        var screen = {}; // id → {x:Float32Array, y:Float32Array} layer-point cache for hit tests
        var rafId = 0;

        function prepare(t) {
            if (t._px && t._px.src === t.sr) return;
            var sr = t.sr, n = sr.n, x = new Float64Array(n), y = new Float64Array(n);
            var prevLon = null, off = 0;
            for (var i = 0; i < n; i++) {
                var lon = sr.lon[i];
                if (prevLon !== null) { // keep a track continuous across the antimeridian
                    var d = lon + off - prevLon;
                    if (d > 180) off -= 360; else if (d < -180) off += 360;
                }
                prevLon = lon + off;
                x[i] = projX(lon + off);
                y[i] = projY(sr.lat[i]);
            }
            t._px = { src: sr, x: x, y: y };
        }

        function reset() {
            var size = map.getSize();
            var padPx = size.multiplyBy(PAD).round();
            var min = map.containerPointToLayerPoint(padPx.multiplyBy(-1)).round();
            bounds = { min: min, size: size.add(padPx.multiplyBy(2)) };
            zoomNow = map.getZoom(); centerNow = map.getCenter();
            L.DomUtil.setPosition(canvas, min);
            var dpr = Math.min(window.devicePixelRatio || 1, 2);
            var W = bounds.size.x, H = bounds.size.y;
            if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(H * dpr)) {
                canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
                canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
            }
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            draw();
        }

        function animateZoom(e) {
            var scale = map.getZoomScale(e.zoom, zoomNow);
            var viewHalf = map.getSize().multiplyBy(0.5 + PAD);
            var cur = map.project(centerNow, e.zoom);
            var off = viewHalf.multiplyBy(-scale).add(cur).subtract(map._getNewPixelOrigin(e.center, e.zoom));
            L.DomUtil.setTransform(canvas, off, scale);
        }

        function toLayer(t) {
            prepare(t);
            var z = Math.pow(2, map.getZoom());
            var po = map.getPixelOrigin();
            var ox = po.x + bounds.min.x, oy = po.y + bounds.min.y;
            var n = t.sr.n, X = new Float32Array(n), Y = new Float32Array(n);
            var px = t._px.x, py = t._px.y;
            // a track may sit in another world copy than the one on screen
            var cx = map.project(map.getCenter()).x / z;
            var shift = Math.round((cx - px[0]) / TILE) * TILE;
            if (Math.abs(cx - px[0]) < TILE / 2) shift = 0;
            for (var i = 0; i < n; i++) { X[i] = (px[i] + shift) * z - ox; Y[i] = py[i] * z - oy; }
            screen[t.id] = { x: X, y: Y };
            return screen[t.id];
        }

        function inSeg(sr) {
            var starts = {};
            for (var k = 0; k < sr.segStart.length; k++) starts[sr.segStart[k]] = 1;
            return starts;
        }

        function drawTrack(t, P) {
            var n = t.sr.n, X = P.x, Y = P.y, starts = inSeg(t.sr);
            var vals = t.values, scale = t.scale;
            var W = 4.2;
            ctx.lineJoin = 'round'; ctx.lineCap = 'round';
            // casing: a dark/light rim keeps any colour legible on any basemap
            ctx.strokeStyle = skin === 'light' ? 'rgba(255,255,255,.9)' : 'rgba(0,0,0,.55)';
            ctx.lineWidth = W + 3;
            strokeRuns(X, Y, n, starts, null);
            if (!vals) {
                ctx.strokeStyle = t.color;
                ctx.lineWidth = W;
                strokeRuns(X, Y, n, starts, null);
                return;
            }
            var cols = scale.diverging ? DIV_C : SEQ_C;
            var lo = scale.min, span = scale.max - scale.min || 1;
            ctx.lineWidth = W;
            var lastB = -1, lx = X[0], ly = Y[0];
            ctx.beginPath();
            ctx.moveTo(lx, ly);
            for (var i = 1; i < n; i++) {
                if (starts[i]) { ctx.stroke(); ctx.beginPath(); ctx.moveTo(X[i], Y[i]); lx = X[i]; ly = Y[i]; continue; }
                var v = vals[i];
                var b = isNaN(v) ? -2 : Math.max(0, Math.min(BUCKETS - 1, Math.round((v - lo) / span * (BUCKETS - 1))));
                if (b !== lastB) {
                    if (lastB !== -1) { ctx.stroke(); ctx.beginPath(); ctx.moveTo(lx, ly); }
                    ctx.strokeStyle = b === -2 ? 'rgba(140,140,140,.8)' : cols[b];
                    lastB = b;
                }
                var dx = X[i] - lx, dy = Y[i] - ly;
                if (dx * dx + dy * dy < 0.5 && i < n - 1) continue;
                ctx.lineTo(X[i], Y[i]);
                lx = X[i]; ly = Y[i];
            }
            ctx.stroke();
        }

        function strokeRuns(X, Y, n, starts, range) {
            var a = range ? range[0] : 0, b = range ? range[1] : n - 1;
            ctx.beginPath();
            ctx.moveTo(X[a], Y[a]);
            var lx = X[a], ly = Y[a];
            for (var i = a + 1; i <= b; i++) {
                if (starts[i]) { ctx.moveTo(X[i], Y[i]); lx = X[i]; ly = Y[i]; continue; }
                var dx = X[i] - lx, dy = Y[i] - ly;
                if (dx * dx + dy * dy < 0.5 && i < b) continue;
                ctx.lineTo(X[i], Y[i]);
                lx = X[i]; ly = Y[i];
            }
            ctx.stroke();
        }

        function dot(x, y, r, fill, stroke) {
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fillStyle = fill; ctx.fill();
            if (stroke) { ctx.lineWidth = 2.5; ctx.strokeStyle = stroke; ctx.stroke(); }
        }
        function flag(x, y) { // chequered finish
            var s = 4;
            ctx.save();
            ctx.beginPath(); ctx.arc(x, y, 9, 0, Math.PI * 2); ctx.fillStyle = '#fff'; ctx.fill();
            ctx.lineWidth = 2; ctx.strokeStyle = '#111'; ctx.stroke();
            ctx.beginPath(); ctx.arc(x, y, 7.5, 0, Math.PI * 2); ctx.clip();
            ctx.fillStyle = '#111';
            for (var i = -2; i < 2; i++) for (var j = -2; j < 2; j++) if ((i + j) & 1) ctx.fillRect(x + i * s, y + j * s, s, s);
            ctx.restore();
        }

        function draw() {
            if (!bounds) return;
            ctx.clearRect(0, 0, bounds.size.x, bounds.size.y);
            screen = {};
            if (heat) { drawHeat(); return; }
            tracks.forEach(function (t) {
                if (!t.visible || !t.sr || t.sr.n < 1) return;
                var P = toLayer(t);
                if (t.sr.n > 1) drawTrack(t, P);
            });
            // climbs: a translucent band under the line, the category at the summit
            if (marks && marks.length) drawMarks();
            // a hovered range (a climb or a split in the list): amber halo
            if (hl && !sel) {
                var ht = byId(hl.id);
                if (ht && ht.visible && screen[ht.id]) {
                    var HS = screen[ht.id];
                    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
                    ctx.strokeStyle = 'rgba(255,176,32,.55)';
                    ctx.lineWidth = 12;
                    strokeRuns(HS.x, HS.y, ht.sr.n, inSeg(ht.sr), [hl.from, hl.to]);
                }
            }
            // selection on top: a bright core over a wide halo
            if (sel) {
                var st = byId(sel.id);
                if (st && st.visible && screen[st.id]) {
                    var S = screen[st.id], starts = inSeg(st.sr);
                    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
                    ctx.strokeStyle = skin === 'light' ? 'rgba(20,20,30,.85)' : 'rgba(255,255,255,.95)';
                    ctx.lineWidth = 9;
                    strokeRuns(S.x, S.y, st.sr.n, starts, [sel.from, sel.to]);
                    ctx.strokeStyle = '#ff2d7a';
                    ctx.lineWidth = 4.5;
                    strokeRuns(S.x, S.y, st.sr.n, starts, [sel.from, sel.to]);
                }
            }
            if (markers) tracks.forEach(function (t) {
                if (!t.visible || !screen[t.id] || !t.sr.n) return;
                var P = screen[t.id], n = t.sr.n;
                if (kmMarks) drawKm(t, P);
                var loop = n > 1 && Math.abs(P.x[0] - P.x[n - 1]) < 14 && Math.abs(P.y[0] - P.y[n - 1]) < 14;
                // a loop: the finish flag sits inside a green start ring instead of hiding it
                if (loop) dot(P.x[n - 1], P.y[n - 1], 13, '#1fb35a', '#fff');
                else dot(P.x[0], P.y[0], 7, '#1fb35a', '#fff');
                if (n > 1) flag(P.x[n - 1], P.y[n - 1]);
            });
            drawWaypoints();
            if (cursor) {
                var ct = byId(cursor.id);
                if (ct && screen[ct.id] && cursor.idx < ct.sr.n) {
                    var C = screen[ct.id];
                    dot(C.x[cursor.idx], C.y[cursor.idx], 7.5, '#ff2d7a', '#fff');
                }
            }
        }

        function drawMarks() {
            // only the tag at the summit: a band under a climb that IS the whole
            // ride (Alpe d'Huez) turns the track itself into mud
            ctx.save();
            ctx.font = '800 12px system-ui,-apple-system,Segoe UI,sans-serif';
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            marks.forEach(function (m) {
                var t = byId(m.id);
                if (!t || !t.visible || !screen[t.id] || !m.label) return;
                var S = screen[t.id], x = S.x[m.to], y = S.y[m.to];
                if (!(x > -30 && y > -30 && x < bounds.size.x + 30 && y < bounds.size.y + 30)) return;
                // beside the summit, not on it: the finish flag and a waypoint
                // pin often stand on the very same point
                var w = Math.max(22, ctx.measureText(m.label).width + 10), lx = x - w - 13;
                ctx.fillStyle = m.color || '#f76b15';
                roundRect(lx, y - 8.5, w, 17, 8.5); ctx.fill();
                ctx.lineWidth = 1.5; ctx.strokeStyle = '#fff'; ctx.stroke();
                ctx.beginPath(); ctx.moveTo(lx + w - 1, y - 4); ctx.lineTo(x - 6, y); ctx.lineTo(lx + w - 1, y + 4); ctx.closePath(); ctx.fill();
                ctx.fillStyle = '#fff';
                ctx.fillText(m.label, lx + w / 2, y + 0.5);
            });
            ctx.restore();
        }
        function hexA(hex, a) {
            var h = String(hex).replace('#', '');
            if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
            return 'rgba(' + parseInt(h.slice(0, 2), 16) + ',' + parseInt(h.slice(2, 4), 16) + ',' + parseInt(h.slice(4, 6), 16) + ',' + a + ')';
        }

        function drawKm(t, P) {
            var d = t.sr.dist, n = t.sr.n;
            if (n < 2) return;
            var unit = kmMarks.unit || 1000;
            // step: 1,2,5,10… units so labels stay ≥ 70 px apart
            var total = d[n - 1] / unit;
            var pxPerUnit = 0, span = 0;
            for (var i = 1; i < n; i++) { var dx = P.x[i] - P.x[i - 1], dy = P.y[i] - P.y[i - 1]; span += Math.sqrt(dx * dx + dy * dy); }
            pxPerUnit = span / Math.max(total, 1e-9);
            var steps = [1, 2, 5, 10, 20, 50, 100, 200, 500], step = steps[steps.length - 1];
            for (var s = 0; s < steps.length; s++) if (pxPerUnit * steps[s] >= 70) { step = steps[s]; break; }
            var next = step, k = 0;
            ctx.font = '700 11px system-ui,-apple-system,Segoe UI,sans-serif';
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            for (var j = 1; j < n && k < 400; j++) {
                if (d[j] / unit >= next) {
                    var x = P.x[j], y = P.y[j];
                    if (x > -20 && y > -20 && x < bounds.size.x + 20 && y < bounds.size.y + 20) {
                        var label = String(next);
                        var w = Math.max(18, ctx.measureText(label).width + 8);
                        ctx.fillStyle = skin === 'light' ? '#fff' : '#1a1d24';
                        ctx.strokeStyle = t.color;
                        ctx.lineWidth = 2;
                        roundRect(x - w / 2, y - 9, w, 18, 9); ctx.fill(); ctx.stroke();
                        ctx.fillStyle = skin === 'light' ? '#111' : '#fff';
                        ctx.fillText(label, x, y + 0.5);
                    }
                    next += step; k++;
                }
            }
        }
        function roundRect(x, y, w, h, r) {
            ctx.beginPath();
            ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y); ctx.arcTo(x + w, y, x + w, y + r, r);
            ctx.lineTo(x + w, y + h - r); ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
            ctx.lineTo(x + r, y + h); ctx.arcTo(x, y + h, x, y + h - r, r);
            ctx.lineTo(x, y + r); ctx.arcTo(x, y, x + r, y, r);
            ctx.closePath();
        }

        var wScreen = [];
        function drawWaypoints() {
            wScreen = [];
            if (!wpts.length) return;
            var z = Math.pow(2, map.getZoom()), po = map.getPixelOrigin();
            var ox = po.x + bounds.min.x, oy = po.y + bounds.min.y;
            var showLabels = wpts.length <= 300 && map.getZoom() >= 11;
            ctx.font = '600 12px system-ui,-apple-system,Segoe UI,sans-serif';
            ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
            wpts.forEach(function (w, i) {
                var x = projX(w.lon) * z - ox, y = projY(w.lat) * z - oy;
                if (x < -40 || y < -40 || x > bounds.size.x + 40 || y > bounds.size.y + 40) return;
                wScreen.push({ i: i, x: x, y: y });
                // pin
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.bezierCurveTo(x - 8, y - 10, x - 8, y - 20, x, y - 20);
                ctx.bezierCurveTo(x + 8, y - 20, x + 8, y - 10, x, y);
                ctx.fillStyle = '#f5a524'; ctx.fill();
                ctx.lineWidth = 1.5; ctx.strokeStyle = skin === 'light' ? '#fff' : '#111'; ctx.stroke();
                ctx.beginPath(); ctx.arc(x, y - 13, 2.6, 0, Math.PI * 2); ctx.fillStyle = '#fff'; ctx.fill();
                if (showLabels && w.name) {
                    var txt = w.name.length > 28 ? w.name.slice(0, 27) + '…' : w.name;
                    ctx.lineWidth = 3.5;
                    ctx.strokeStyle = skin === 'light' ? 'rgba(255,255,255,.95)' : 'rgba(10,12,16,.9)';
                    ctx.strokeText(txt, x + 9, y - 13);
                    ctx.fillStyle = skin === 'light' ? '#1b1b1f' : '#f3f4f6';
                    ctx.fillText(txt, x + 9, y - 13);
                }
            });
        }

        function drawHeat() {
            var z = Math.pow(2, map.getZoom()), po = map.getPixelOrigin();
            var ox = po.x + bounds.min.x, oy = po.y + bounds.min.y;
            ctx.save();
            ctx.globalCompositeOperation = skin === 'light' ? 'multiply' : 'lighter';
            var a = heat.lines.length > 400 ? 0.12 : heat.lines.length > 100 ? 0.2 : 0.35;
            ctx.strokeStyle = skin === 'light' ? 'rgba(225,40,90,' + a + ')' : 'rgba(255,90,60,' + a + ')';
            ctx.lineWidth = map.getZoom() >= 13 ? 3 : 2;
            ctx.lineJoin = 'round'; ctx.lineCap = 'round';
            heat.lines.forEach(function (ln) {
                if (ln.hidden) return;
                var n = ln.lat.length;
                if (n < 2) return;
                heatProj(ln);
                ctx.beginPath();
                for (var i = 0; i < n; i++) {
                    var x = ln._x[i] * z - ox, y = ln._y[i] * z - oy;
                    if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y);
                }
                ctx.stroke();
            });
            ctx.restore();
        }

        function byId(id) { for (var i = 0; i < tracks.length; i++) if (tracks[i].id === id) return tracks[i]; return null; }

        function schedule() {
            if (rafId) return;
            rafId = requestAnimationFrame(function () { rafId = 0; draw(); });
        }

        // ── pointer: nearest point within reach ─────────────────────────────
        function nearest(cp) {
            if (!bounds) return null;
            var lx = cp.x + (-bounds.min.x) + map.containerPointToLayerPoint([0, 0]).x;
            var ly = cp.y + (-bounds.min.y) + map.containerPointToLayerPoint([0, 0]).y;
            var best = null, bd = 16 * 16;
            tracks.forEach(function (t) {
                var S = screen[t.id];
                if (!t.visible || !S) return;
                var X = S.x, Y = S.y;
                for (var i = 0; i < X.length; i++) {
                    var dx = X[i] - lx, dy = Y[i] - ly, d = dx * dx + dy * dy;
                    if (d < bd) { bd = d; best = { id: t.id, idx: i }; }
                }
            });
            return best;
        }
        function nearestWpt(cp) {
            var lx = cp.x - bounds.min.x + map.containerPointToLayerPoint([0, 0]).x;
            var ly = cp.y - bounds.min.y + map.containerPointToLayerPoint([0, 0]).y;
            for (var k = wScreen.length - 1; k >= 0; k--) {
                var w = wScreen[k];
                if (Math.abs(w.x - lx) <= 9 && ly <= w.y + 2 && ly >= w.y - 22) return w.i;
            }
            return -1;
        }
        var hoverRaf = 0, lastMove = null;
        map.on('mousemove', function (e) {
            lastMove = e.containerPoint;
            if (hoverRaf || heat) return;
            hoverRaf = requestAnimationFrame(function () {
                hoverRaf = 0;
                var h = nearest(lastMove);
                map.getContainer().style.cursor = h || nearestWpt(lastMove) >= 0 ? 'pointer' : '';
                if (opts.onHover) opts.onHover(h);
            });
        });
        map.on('mouseout', function () { if (opts.onHover) opts.onHover(null); });
        map.on('click', function (e) {
            if (heat) { if (opts.onHeatClick) opts.onHeatClick(heatHit(e.containerPoint)); return; }
            var w = nearestWpt(e.containerPoint);
            if (w >= 0 && opts.onWaypoint) { opts.onWaypoint(w, wpts[w]); return; }
            var h = nearest(e.containerPoint);
            if (opts.onClick) opts.onClick(h, e);
        });
        function heatProj(ln) {
            if (ln._x) return;
            var n = ln.lat.length;
            ln._x = new Float64Array(n); ln._y = new Float64Array(n);
            for (var i = 0; i < n; i++) { ln._x[i] = projX(ln.lon[i]); ln._y[i] = projY(ln.lat[i]); }
        }
        function heatHit(cp) {
            var z = Math.pow(2, map.getZoom()), po = map.getPixelOrigin();
            var lp = map.containerPointToLayerPoint(cp);
            var best = null, bd = 10 * 10;
            heat.lines.forEach(function (ln, k) {
                if (ln.hidden) return;
                heatProj(ln);
                for (var i = 0; i < ln.lat.length; i++) {
                    var x = ln._x[i] * z - po.x, y = ln._y[i] * z - po.y;
                    var dx = x - lp.x, dy = y - lp.y, d = dx * dx + dy * dy;
                    if (d < bd) { bd = d; best = k; }
                }
            });
            return best;
        }

        map.on('zoomanim', animateZoom);
        map.on('moveend zoomend resize viewreset', reset);
        reset();

        return {
            setTracks: function (list) { tracks = list; draw(); },
            setSelection: function (id, from, to) { sel = id == null ? null : { id: id, from: Math.min(from, to), to: Math.max(from, to) }; schedule(); },
            setCursor: function (id, idx) { cursor = id == null ? null : { id: id, idx: idx }; schedule(); },
            setHighlight: function (id, from, to) { hl = id == null ? null : { id: id, from: Math.min(from, to), to: Math.max(from, to) }; schedule(); },
            setMarks: function (list) { marks = list && list.length ? list : null; schedule(); },
            setWaypoints: function (list) { wpts = list || []; schedule(); },
            setSkin: function (s) { skin = s === 'light' ? 'light' : 'dark'; schedule(); },
            setMarkers: function (on) { markers = !!on; schedule(); },
            setKm: function (k) { kmMarks = k; schedule(); },
            setHeat: function (h) { heat = h; schedule(); },
            redraw: schedule,
            fit: function (ids, padding) {
                var la0 = 90, la1 = -90, lo0 = 540, lo1 = -540, any = false;
                if (heat) {
                    heat.lines.forEach(function (ln) { if (ln.hidden) return; for (var i = 0; i < ln.lat.length; i++) { any = true; la0 = Math.min(la0, ln.lat[i]); la1 = Math.max(la1, ln.lat[i]); lo0 = Math.min(lo0, ln.lon[i]); lo1 = Math.max(lo1, ln.lon[i]); } });
                } else tracks.forEach(function (t) {
                    if (!t.visible || !t.sr || (ids && ids.indexOf(t.id) < 0)) return;
                    var sr = t.sr, a = 0, b = sr.n - 1;
                    if (ids && sel && sel.id === t.id && ids.length === 1 && opts.fitSelection) { a = sel.from; b = sel.to; }
                    for (var i = a; i <= b; i++) { any = true; la0 = Math.min(la0, sr.lat[i]); la1 = Math.max(la1, sr.lat[i]); lo0 = Math.min(lo0, sr.lon[i]); lo1 = Math.max(lo1, sr.lon[i]); }
                });
                if (!any) wpts.forEach(function (w) { any = true; la0 = Math.min(la0, w.lat); la1 = Math.max(la1, w.lat); lo0 = Math.min(lo0, w.lon); lo1 = Math.max(lo1, w.lon); });
                if (!any) return;
                if (la0 === la1 && lo0 === lo1) { map.setView([la0, lo0], 15); return; }
                map.fitBounds([[la0, lo0], [la1, lo1]], { padding: padding || [28, 28], maxZoom: 17 });
            },
            fitRange: function (id, from, to) {
                var t = byId(id);
                if (!t) return;
                var sr = t.sr, la0 = 90, la1 = -90, lo0 = 540, lo1 = -540;
                for (var i = from; i <= to; i++) { la0 = Math.min(la0, sr.lat[i]); la1 = Math.max(la1, sr.lat[i]); lo0 = Math.min(lo0, sr.lon[i]); lo1 = Math.max(lo1, sr.lon[i]); }
                map.fitBounds([[la0, lo0], [la1, lo1]], { padding: [40, 40], maxZoom: 17 });
            },
            canvas: canvas,
            _draw: draw
        };
    }

    root.GpsMapLayer = { create: create, projX: projX, projY: projY, ramp: ramp, SEQ: SEQ, DIV: DIV };
})();
